import { loadBrandingFromStorage, resetCompanyBranding } from "@/_helpers";

const STORAGE_KEY = "pf24-accessibility-preferences";

export const ACCESSIBILITY_TOOLBAR_EVENT = "pf-open-accessibility-toolbar";

export const TEXT_SCALE_STEPS = [1, 1.25, 1.5, 1.75, 2];
export const TEXT_SCALE_CLASSES = [
  "a11y-text-scale-100",
  "a11y-text-scale-125",
  "a11y-text-scale-150",
  "a11y-text-scale-175",
  "a11y-text-scale-200",
];

export const defaultPreferences = {
  textScaleIndex: 0,
  accessibleColors: false,
  bigCursor: false,
  focusHighlight: false,
  easyRead: false,
  reducedMotion: false,
};

const isBrowser = typeof window !== "undefined";

const domElements = () => ({
  body: isBrowser ? document.body : null,
  html: isBrowser ? document.documentElement : null,
});

export function loadPreferences() {
  if (!isBrowser) {
    return { ...defaultPreferences };
  }

  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return {
      ...defaultPreferences,
      ...(saved || {}),
      textScaleIndex: clampScaleIndex(saved?.textScaleIndex ?? 0),
    };
  } catch (error) {
    console.warn("Impossibile caricare le preferenze di accessibilità", error);
    return { ...defaultPreferences };
  }
}

export function persistPreferences(preferences) {
  if (!isBrowser) {
    return;
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
}

export function clampScaleIndex(index) {
  if (typeof index !== "number" || Number.isNaN(index)) {
    return 0;
  }

  return Math.min(Math.max(index, 0), TEXT_SCALE_STEPS.length - 1);
}

export function getTextScaleLabel(index) {
  const value = TEXT_SCALE_STEPS[clampScaleIndex(index)] ?? 1;
  return Math.round(value * 100);
}

export function applyPreferencesToDom(preferences) {
  if (!isBrowser) {
    return;
  }

  const { body, html } = domElements();
  if (!body || !html) {
    return;
  }

  html.classList.remove(...TEXT_SCALE_CLASSES);
  html.classList.add(
    TEXT_SCALE_CLASSES[clampScaleIndex(preferences.textScaleIndex)]
  );

  toggleClass(body, "a11y-accessible-colors", preferences.accessibleColors);
  syncBrandingState(preferences.accessibleColors);
  toggleClass(body, "a11y-big-cursor", preferences.bigCursor);
  toggleClass(body, "a11y-focus-highlight", preferences.focusHighlight);
  toggleClass(body, "a11y-easy-read", preferences.easyRead);
  toggleClass(body, "a11y-reduced-motion", preferences.reducedMotion);
}

function syncBrandingState(accessibleColorsEnabled) {
  if (accessibleColorsEnabled) {
    resetCompanyBranding();
    return;
  }

  loadBrandingFromStorage();
}

function toggleClass(target, className, shouldAdd) {
  if (!target) {
    return;
  }

  target.classList.toggle(className, Boolean(shouldAdd));
}

export function saveAndApplyPreferences(preferences) {
  persistPreferences(preferences);
  applyPreferencesToDom(preferences);
}

export function resetPreferences() {
  saveAndApplyPreferences({ ...defaultPreferences });
  return { ...defaultPreferences };
}

export function describePreference(key, value) {
  const normalizedValue = typeof value === "boolean" ? value : Boolean(value);
  return { key, value: normalizedValue };
}

export function useFocusElement(selector = "[data-page-focus]") {
  if (!isBrowser) {
    return;
  }

  window.requestAnimationFrame(() => {
    const focusTarget = document.querySelector(selector);
    if (focusTarget) {
      if (!focusTarget.hasAttribute("tabindex")) {
        focusTarget.setAttribute("tabindex", "-1");
      }
      focusTarget.focus({ preventScroll: true });
    }
  });
}

export function requestAccessibilityToolbarFocus() {
  if (!isBrowser) {
    return;
  }

  window.dispatchEvent(new CustomEvent(ACCESSIBILITY_TOOLBAR_EVENT));
}
